import { APP_INITIALIZER, InjectionToken, Inject, Optional, Provider, Type } from '@angular/core';

/**
 * Multi-provider token that prevents esbuild/webpack from tree-shaking dynamic
 * components during production builds.
 *
 * Problem: `@NgModule.declarations` is a compile-time AOT scope entry. esbuild
 * may eliminate modules that have no live JavaScript value reference in the
 * emitted bundle, causing class-definition-time decorator side-effects
 * (`@WizardPlugin`, `@CloudResource`) never to run, breaking auto-discovery.
 *
 * Solution: `{ provide: DYNAMIC_COMPONENTS_RETENTION, useValue: [ComponentA, …] }`
 * generates a raw JS array reference in the compiled output. esbuild treats this
 * as a live reference and cannot eliminate those class modules.
 *
 * Usage in app.module.ts providers:
 * ```ts
 * {
 *   provide: DYNAMIC_COMPONENTS_RETENTION,
 *   useValue: [CloudProviderPluginComponent, SoftwareConfigPluginComponent, …]
 * }
 * ```
 *
 * The `DecoratorFactory.provider` APP_INITIALIZER injects this token and uses it
 * only as a "keep-alive" anchor — it performs no additional registration work
 * because the decorator side-effects will already have populated the static map
 * by the time the initializer runs.
 */
export const DYNAMIC_COMPONENTS_RETENTION = new InjectionToken<Type<IDecoratedComponent>[]>(
  'DYNAMIC_COMPONENTS_RETENTION'
);
import { DecoratorService } from './decorator.service';
import { IDecoratedComponent } from './decorated-component.interface';
import { DecoratorConfig } from './decorator-config';
import { IShortLogger, Log } from '../services/logger.service';

/**
 * Optimized universal registration system with static methods for decorator creation,
 * component registration, and provider management.
 *
 * Uses a single source of truth pattern with lazy projections for optimal performance.
 */
export class DecoratorFactory {
  /**
   * Main data structure: Maps decorator names to Maps of components
   * This is the single source of truth for all registrations
   * Format: Map<decoratorName, Map<componentType, componentName>>
   */
  private static readonly componentsByDecorator: Map<string, Map<Type<IDecoratedComponent>, string>> = new Map();

  /**
   * Cache for created decorators to prevent duplicates
   */
  private static readonly decoratorCache: Map<string, any> = new Map();

  /**
   * Static logger for decorator system logging
   */
  private static readonly logger: IShortLogger<DecoratorFactory> = Log.create(DecoratorFactory);


  /**
   * Lazy-loaded projection of all components from componentsByDecorator
   * Returns a Map of componentName -> componentType for O(1) lookups
   */
  static get componentRegistry(): Map<string, Type<IDecoratedComponent>> {
    const registry = new Map<string, Type<IDecoratedComponent>>();

    for (const decoratorMap of this.componentsByDecorator.values()) {
      for (const [componentType, componentName] of decoratorMap.entries()) {
        registry.set(componentName, componentType);
      }
    }

    return registry;
  }

  /**
   * Create a decorator for a specific configuration type
   * Uses caching to ensure each registry name gets only one decorator function
   *
   * @param registryName The name of the registry (used as config key)
   * @returns Decorator function for the specified configuration
   */
  static create<TConfig extends DecoratorConfig>(registryName: string) {
    const { normalizedKey } = this.normalizer(registryName);

    // Check if we already have a decorator for this registry name
    if (this.decoratorCache.has(normalizedKey)) {
      this.logger.debug( `Using cached decorator for registry: ${normalizedKey}`);
      return this.decoratorCache.get(normalizedKey) as (config: TConfig) => (targetComponent: Type<any>) => Type<any>;
    }

    // Create new decorator and cache it
    const decorator = this.createUniversalDecorator<TConfig>(registryName);
    this.decoratorCache.set(normalizedKey, decorator);

    this.logger.debug( `Created new decorator for registry: ${normalizedKey}`);
    return decorator;
  }

  /**
   * Get the universal provider for APP_INITIALIZER
   * Uses optimized batch registration pattern.
   *
   * Also injects DYNAMIC_COMPONENTS_RETENTION so that esbuild/webpack sees a
   * live JavaScript reference to every dynamically-loaded component class,
   * preventing them from being tree-shaken in optimized production builds.
   * The injected value itself is intentionally unused beyond this anchor role.
   */
  static readonly provider: Provider = {
    provide: APP_INITIALIZER,
    useFactory: (
      decorationService: DecoratorService,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _retention: Type<IDecoratedComponent>[] | null
    ) => {
      return () => {
        this.logger.debug( `Registering components from ${this.componentsByDecorator.size} decorators`);

        // Register each decorator and its components in batch
        this.componentsByDecorator.forEach((componentMap, decoratorName) => {
          componentMap.forEach((componentName, componentType) => {
            decorationService.registerComponentWithDecorator(decoratorName, componentType);
          });
        });

        return Promise.resolve();
      };
    },
    deps: [DecoratorService, [new Optional(), new Inject(DYNAMIC_COMPONENTS_RETENTION)]],
    multi: true
  };

  /**
   * Get all cached decorator registry names
   * Useful for debugging and introspection
   *
   * @returns Array of registry names that have been created
   */
  static getCachedRegistryNames(): string[] {
    return Array.from(this.decoratorCache.keys());
  }

  /**
   * Get all registered components
   *
   * @returns Set of registered component types
   */
  static getRegisteredComponents(): Set<Type<IDecoratedComponent>> {
    const components = new Set<Type<IDecoratedComponent>>();

    for (const decoratorMap of this.componentsByDecorator.values()) {
      for (const componentType of decoratorMap.keys()) {
        components.add(componentType);
      }
    }

    return components;
  }

  /**
   * Get all components decorated with a specific decorator
   *
   * @param decoratorName The decorator name to search for
   * @returns Set of component types decorated with the specified decorator
   */
  static getDecoratedComponents(decoratorName: string): Set<Type<IDecoratedComponent>> {
    const { normalizedKey } = this.normalizer(decoratorName);
    const decoratorMap = this.componentsByDecorator.get(normalizedKey);

    if (!decoratorMap) {
      return new Set();
    }

    return new Set(decoratorMap.keys());
  }

  /**
   * Get all decorator names that have been used
   *
   * @returns Array of decorator names
   */
  static getAllDecoratorNames(): string[] {
    return Array.from(this.componentsByDecorator.keys());
  }

  /**
   * Get a registered component by name
   *
   * @param componentName The name of the component to find
   * @returns The component type or undefined if not found
   */
  static getComponentByName(componentName: string): Type<IDecoratedComponent> | undefined {
    return this.componentRegistry.get(componentName);
  }

  /**
   * Get all registered component names
   *
   * @returns Array of component names
   */
  static getRegisteredComponentNames(): string[] {
    return Array.from(this.componentRegistry.keys());
  }

  /**
   * Clear all caches
   * Use with caution in production code
   */
  static clearCache(): void {
    this.decoratorCache.clear();
    this.componentsByDecorator.clear();
    this.logger.debug( 'Decoration Registry cache cleared');
  }

  /**
   * Creates a centralized key normalization system for consistent registry key handling.
   *
   * @param registryName The registry name to normalize
   * @returns An object containing normalization functions
   *
   * @private
   */
  private static normalizer(registryName: string): {
    normalizeKey: (key: string) => string,
    normalizedKey: string,
  } {
    const normalizer = (key: string): string => key.toLowerCase().trim();
    const normalizedKey = normalizer(registryName);
    return {
      normalizeKey: normalizer,
      normalizedKey
    };
  }

  /**
   * Create the actual decorator function for a registry type
   *
   * @param registryName The registry name
   * @returns Decorator function
   */
  private static createUniversalDecorator<TConfig extends DecoratorConfig>(
    registryName: string
  ): (config: TConfig) => (targetComponent: Type<any>) => Type<any> {
    const { normalizeKey, normalizedKey } = this.normalizer(registryName);

    this.logger.debug( `Creating decorator function for registry: ${normalizedKey}`);

    return (config: TConfig) => {
      this.logger.debug( `Decorator function called for ${normalizedKey} with config:`, config);

      return (targetComponent: Type<any>) => {
        this.logger.debug( `Decorating component ${targetComponent.name} with ${normalizedKey}`);

        // Set default values
        config.componentName = targetComponent.name;

        // Add to componentsByDecorator (single source of truth)
        if (!this.componentsByDecorator.has(normalizedKey)) {
          this.componentsByDecorator.set(normalizedKey, new Map());
          this.logger.debug( `Created new decorator map for ${normalizedKey}`);
        }

        const decoratorMap = this.componentsByDecorator.get(normalizedKey)!;

        if (decoratorMap.has(targetComponent)) {
          this.logger.debug( `Component ${targetComponent.name} already decorated with ${normalizedKey}, skipping duplicate`);
        } else {
          decoratorMap.set(targetComponent, targetComponent.name);
          this.logger.debug( `Successfully added ${targetComponent.name} to ${normalizedKey} decorator registry`);
        }

        // Initialize componentConfigs map if it doesn't exist
        if (!targetComponent.prototype.componentConfigs) {
          targetComponent.prototype.componentConfigs = new Map<string, DecoratorConfig>();
        }

        // Add the configuration to the map
        targetComponent.prototype.componentConfigs.set(normalizedKey, config);

        // Add query methods to the prototype if they don't exist
        this.addQueryMethods(targetComponent, normalizeKey);

        this.logger.debug( `Finished decorating ${targetComponent.name} with ${normalizedKey}`);
        return targetComponent;
      };
    };
  }

  /**
   * Add query methods to component prototype if they don't already exist
   *
   * @param targetComponent The component to add methods to
   * @param normalizeKey The normalization function to use consistently
   */
  private static addQueryMethods(
    targetComponent: Type<any>,
    normalizeKey: (key: string) => string
  ): void {
    // getAllConfigs - Get all configurations
    if (!targetComponent.prototype.getAllConfigs) {
      targetComponent.prototype.getAllConfigs = function (): Map<string, DecoratorConfig> {
        return new Map(this.componentConfigs || new Map());
      };
    }

    // getConfigsByKeys - Get configurations for specific keys
    if (!targetComponent.prototype.getConfigsByKeys) {
      targetComponent.prototype.getConfigsByKeys = function (keys: string[]): Map<string, DecoratorConfig> {
        const result = new Map<string, DecoratorConfig>();
        if (!this.componentConfigs) return result;

        for (const key of keys) {
          const normalizedKey = normalizeKey(key);
          if (this.componentConfigs.has(normalizedKey)) {
            result.set(normalizedKey, this.componentConfigs.get(normalizedKey)!);
          }
        }
        return result;
      };
    }

    // getConfigByKey - Get a single configuration by key (case-insensitive)
    if (!targetComponent.prototype.getConfigByKey) {
      targetComponent.prototype.getConfigByKey = function <T extends DecoratorConfig>(key: string): T | undefined {
        if (!this.componentConfigs) return undefined;
        const normalizedKey = normalizeKey(key);
        return this.componentConfigs.get(normalizedKey) as T | undefined;
      };
    }

    // hasConfigFor - Check if configuration exists for key (case-insensitive)
    if (!targetComponent.prototype.hasConfigFor) {
      targetComponent.prototype.hasConfigFor = function (key: string): boolean {
        if (!this.componentConfigs) return false;
        const normalizedKey = normalizeKey(key);
        return this.componentConfigs.has(normalizedKey);
      };
    }

    // getConfigKeys - Get all configuration keys (lowercase)
    if (!targetComponent.prototype.getConfigKeys) {
      targetComponent.prototype.getConfigKeys = function (): string[] {
        if (!this.componentConfigs) return [];
        return Array.from(this.componentConfigs.keys());
      };
    }
  }
}
